// Financial Freedom Calculator -- reusable dual-control input.
//
// Syncs a visual HTML range slider with a precise text input so a user can
// either drag for quick exploration or type an exact figure. The text handler
// strips non-numeric characters (so pasting "10k" degrades gracefully to a
// clean numeric state) while the range input stays perfectly in sync.

import React from 'react';

interface SliderInputProps {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    prefix?: string;
    suffix?: string;
    onChange: (val: number) => void;
}

export const SliderInput: React.FC<SliderInputProps> = ({
    label,
    value,
    min,
    max,
    step = 1,
    prefix = '',
    suffix = '',
    onChange,
}) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Strip non-numeric characters for a clean UX.
        const rawVal = e.target.value.replace(/[^0-9.-]/g, '');
        if (rawVal === '') {
            onChange(0);
            return;
        }
        const numericVal = parseFloat(rawVal);
        if (!isNaN(numericVal)) onChange(numericVal);
    };

    return (
        <div className="mb-6 w-full">
            <div className="flex justify-between items-center mb-2 gap-3">
                <label className="text-sm font-semibold text-slate-700">{label}</label>
                <div className="relative shrink-0">
                    {prefix && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium pointer-events-none">
                            {prefix}
                        </span>
                    )}
                    <input
                        type="text"
                        inputMode="decimal"
                        value={value}
                        onChange={handleInputChange}
                        className={`w-32 text-right p-1.5 border border-slate-300 rounded-md text-sm font-bold text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 focus:outline-none transition-colors ${
                            prefix ? 'pl-9' : 'pl-2'
                        } ${suffix ? 'pr-9' : 'pr-2'}`}
                    />
                    {suffix && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium pointer-events-none">
                            {suffix}
                        </span>
                    )}
                </div>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1 font-medium">
                <span>
                    {prefix}
                    {min.toLocaleString()}
                    {suffix}
                </span>
                <span>
                    {prefix}
                    {max.toLocaleString()}
                    {suffix}
                </span>
            </div>
        </div>
    );
};
